import * as THREE from 'three';
import URDFLoader from 'urdf-loader';

/**
 * URRobot - Official Universal Robots 3D model using URDF
 * 
 * Loads official Universal Robots mesh files using urdf-loader which properly
 * handles all coordinate system transforms and joint hierarchies.
 * 
 * Features:
 * - Supports multiple UR models (UR10e, UR20, etc.)
 * - Official UR mesh geometry
 * - Proper URDF-based kinematics
 * - Configurable joint angles
 */
export class UR10Robot {
  constructor(robotType = 'ur10e') {
    this.robotType = robotType;
    this.robot = null;
    
    // Current joint angles (radians)
    this.jointAngles = [0, 0, 0, 0, 0, 0];
    
    // Joint names
    this.jointNames = [
      'shoulder_pan_joint',
      'shoulder_lift_joint',
      'elbow_joint',
      'wrist_1_joint',
      'wrist_2_joint',
      'wrist_3_joint'
    ];
    
    // Track loading state
    this.isLoaded = false;
    
    this.buildRobot();
  }
  
  /**
   * Build robot using URDF loader
   */
  async buildRobot() {
    const loader = new URDFLoader();
    
    // Set up package path resolver for mesh files
    loader.packages = {
      '': `/meshes/${this.robotType}`  // Maps package:// to our mesh directory
    };
    
    // Configure loader to work correctly
    loader.workingPath = `/meshes/${this.robotType}/`;
    
    try {
      console.log(`Loading ${this.robotType.toUpperCase()} URDF from official Universal Robots parameters...`);
      
      // Load the URDF
      this.robot = await loader.loadAsync(`/meshes/${this.robotType}/${this.robotType}.urdf`);
      
      console.log(`${this.robotType.toUpperCase()} URDF loaded successfully!`);
      console.log('Available joints:', Object.keys(this.robot.joints));
      
      // Apply material to all meshes
      this.robot.traverse((child) => {
        if (child.isMesh) {
          child.material = new THREE.MeshStandardMaterial({
            color: 0x7C98AB, // Official UR grey-blue
            metalness: 0.5,
            roughness: 0.5
          });
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      
      // Apply coordinate transform from ROS (X=forward, Y=left, Z=up) to Three.js (X=right, Y=up, Z=forward)
      // We need to map:
      //   ROS X (forward) → Three.js Z (forward)
      //   ROS Y (left) → Three.js -X (left = negative right)
      //   ROS Z (up) → Three.js Y (up)
      // This can be achieved with: Rotate -90° around X, then -90° around Z
      this.robot.rotation.order = 'XYZ';
      this.robot.rotation.x = -Math.PI / 2;  // Z-up becomes Y-up
      // this.robot.rotation.z = -Math.PI / 2;  // Y-left becomes -X (and X-forward becomes Z-forward)
      
      this.isLoaded = true;
      console.log(`${this.robotType.toUpperCase()} ready! Applied ROS→Three.js coordinate transform`);
      
      // Verify all joints loaded correctly
      const missingJoints = this.jointNames.filter(name => !this.robot.joints[name]);
      if (missingJoints.length > 0) {
        console.warn('Missing joints:', missingJoints);
      }
      
      // Add default TCP frame immediately (no tool0 frame)
      this.setDefaultTCPFrame();
      
    } catch (error) {
      console.error('Error loading UR10 URDF:', error);
      this.robot = new THREE.Group();
      this.robot.name = 'UR10Robot_fallback';
    }
  }
  
  /**
   * Set default TCP frame (TCP_Vectis) on robot load
   */
  setDefaultTCPFrame() {
    const defaultTCPOffset = {
      x: 0.002880472034598727,
      y: -0.02918177960863152,
      z: 0.47236807083603005,
      rx: -0.8846974644582652,
      ry: 0.010026373014888897,
      rz: -0.005155957771068098
    };
    this.setTCPFrame(defaultTCPOffset);
    console.log('Default TCP frame applied');
  }
  
  /**
   * Add or update TCP (Tool Center Point) frame visualization
   * @param {Object} tcpOffset - TCP offset {x, y, z, rx, ry, rz} where xyz is in meters and rxryrz is rotation vector (axis-angle) in radians
   */
  setTCPFrame(tcpOffset) {
    const tool0 = this.robot.links?.tool0;
    if (!tool0) {
      console.warn('Could not find tool0 link for TCP frame');
      return;
    }

    // Remove existing TCP frame if present
    const existingTCP = tool0.children.find(child => child.name === 'tcp_frame');
    if (existingTCP) {
      tool0.remove(existingTCP);
    }

    // Create TCP frame group
    const tcpFrame = new THREE.Group();
    tcpFrame.name = 'tcp_frame';

    // Set position (xyz in meters)
    tcpFrame.position.set(tcpOffset.x, tcpOffset.y, tcpOffset.z);

    // Set rotation from axis-angle representation
    // The rotation is given as a rotation vector [rx, ry, rz] where:
    // - Direction = axis of rotation
    // - Magnitude = angle in radians
    const angle = Math.sqrt(tcpOffset.rx ** 2 + tcpOffset.ry ** 2 + tcpOffset.rz ** 2);
    if (angle > 0.0001) {
      const axis = new THREE.Vector3(
        tcpOffset.rx / angle,
        tcpOffset.ry / angle,
        tcpOffset.rz / angle
      );
      tcpFrame.setRotationFromAxisAngle(axis, angle);
    }

    // Add axes helper to show TCP frame (slightly larger than tool0 frame for visibility)
    const tcpAxesHelper = new THREE.AxesHelper(0.20);
    tcpAxesHelper.name = 'tcp_axes';
    tcpFrame.add(tcpAxesHelper);

    // Add TCP frame to tool0 link
    tool0.add(tcpFrame);
    console.log('TCP frame added at offset:', tcpOffset);
  }

  /**
   * Remove TCP frame visualization
   */
  removeTCPFrame() {
    const tool0 = this.robot.links?.tool0;
    if (!tool0) return;

    const existingTCP = tool0.children.find(child => child.name === 'tcp_frame');
    if (existingTCP) {
      tool0.remove(existingTCP);
      console.log('TCP frame removed');
    }
  }
  


  
  /**
   * Get the robot group to add to scene
   */
  getModel() {
    return this.robot;
  }
  
  /**
   * Set the robot's position
   */
  setPosition(x, y, z) {
    this.robot.position.set(x, y, z);
  }
  
  /**
   * Set the robot's rotation
   */
  setRotation(x, y, z) {
    this.robot.rotation.set(x, y, z);
  }
  
  /**
   * Set the robot's scale
   */
  setScale(scale) {
    this.robot.scale.set(scale, scale, scale);
  }
  
  /**
   * Show/hide the robot
   */
  setVisible(visible) {
    this.robot.visible = visible;
  }
  
  /**
   * Set joint angles and update robot pose using forward kinematics
   * @param {Array<number>} angles - Array of 6 joint angles in DEGREES
   */
  setJointAngles(angles) {
    if (angles.length !== 6) {
      console.error('Expected 6 joint angles, got', angles.length);
      return;
    }
    
    // Convert degrees to radians
    this.jointAngles = angles.map(deg => deg * Math.PI / 180);
    
    // Apply forward kinematics
    this.updateFK();
  }
  
  /**
   * Set joint angles in radians
   * @param {Array<number>} angles - Array of 6 joint angles in radians
   */
  setJointAnglesRadians(angles) {
    if (angles.length !== 6) {
      console.error('Expected 6 joint angles, got', angles.length);
      return;
    }
    
    this.jointAngles = [...angles];
    this.updateFK();
  }
  
  /**
   * Update forward kinematics by setting joint angles
   */
  updateFK() {
    if (!this.isLoaded || !this.robot.joints) return;
    
    // URDFLoader creates a joints object with named joints
    this.jointNames.forEach((name, i) => {
      const joint = this.robot.joints[name];
      if (joint) {
        joint.setJointValue(this.jointAngles[i]);
      }
    });
  }
  
  /**
   * Get current joint angles in degrees
   */
  getJointAngles() {
    return this.jointAngles.map(rad => rad * 180 / Math.PI);
  }
  
  /**
   * Get current joint angles in radians
   */
  getJointAnglesRadians() {
    return [...this.jointAngles];
  }
  
  /**
   * Helper to get depth of object in scene hierarchy
   */
  getDepth(object) {
    let depth = 0;
    let current = object;
    while (current.parent) {
      depth++;
      current = current.parent;
    }
    return depth;
  }
}
